import { z } from 'zod'
import { ISODate, Uuid, Version, blankAsAbsent, moneyField } from './primitives.ts'
import { PageQuery } from './pagination.ts'
import { BULK_COST_IMPORT_MAX, BULK_TOO_MANY, ORDER_BULK_MAX } from '../order-board.ts'

/**
 * ══ HỢP ĐỒNG API — CHI PHÍ ĐƠN HÀNG · LÃI GỘP (F14) ═══════════════════════════════════════════
 * Nguồn sự thật duy nhất cho `/api/v1/az/costs`.
 *
 * ⚠ MỌI cửa dưới đây là GIÁ VỐN — kể cả cửa ĐỌC. Lược đồ không quyết định điều đó (quyền mới
 *   quyết định), nhưng đây là chỗ đầu tiên người đọc mã gặp, nên ghi ra cho rõ.
 */

export const CostKind = z.enum(['freight', 'other', 'vendor_advance', 'onbehalf', 'joborder'])
export type CostKind = z.infer<typeof CostKind>

export const PayStatus = z.enum(['draft', 'acc_confirmed', 'requested', 'paid'])
export const DocStatus = z.enum(['missing', 'received', 'original'])
export const PayeeKind = z.enum(['customer', 'vendor', 'staff'])

/** Ô có ở CẢ tạo lẫn sửa. Ô trống trên form gửi lên là chuỗi rỗng ⇒ `blankAsAbsent` bỏ hẳn khoá. */
const costFields = {
  legId: blankAsAbsent(Uuid),
  vendorId: blankAsAbsent(Uuid),
  payeeKind: blankAsAbsent(PayeeKind),
  payeeId: blankAsAbsent(Uuid),
  payeeName: blankAsAbsent(z.string()),
  priceMode: blankAsAbsent(z.enum(['bid', 'nego'])),
  settleMode: blankAsAbsent(z.enum(['immediate', 'monthly'])),
  expenseItem: blankAsAbsent(z.string()),
  debitAccount: blankAsAbsent(z.string()),
  creditAccount: blankAsAbsent(z.string()),
  whtRate: z.number().min(0).max(100).optional(),
  vatRate: z.number().min(0).max(100).optional(),
  invoiceNo: blankAsAbsent(z.string()),
  invoiceDate: blankAsAbsent(ISODate),
  invoiceSymbol: blankAsAbsent(z.string()),
  mgmtOnly: z.boolean().optional(),
  docStatus: blankAsAbsent(DocStatus),
  note: blankAsAbsent(z.string()),
}

export const CreateCostBody = z.object({
  jobId: Uuid,
  kind: CostKind,
  label: z.string().min(1, 'Chưa đặt tên khoản chi phí.'),
  amount: moneyField('Số tiền'),
  ...costFields,
  /** Dòng giá vốn đã chọn khi nguồn giá là "chào thầu" (bản cũ `bidRateId`, `Az.tsx:10407`) — bộ soát giá đọc bước ② theo nó (H01719). */
  bidRateId: blankAsAbsent(Uuid),
})

export const UpdateCostBody = z.object({
  version: Version,
  label: blankAsAbsent(z.string()),
  amount: blankAsAbsent(moneyField('Số tiền')),
  ...costFields,
  /** Sửa số tiền từ ô nào — lịch sử ghi "Sửa phụ phí / Sửa giá vốn: X → Y đ" như bản cũ (`Az.tsx:8722` · `:10185`). */
  amountLog: blankAsAbsent(z.enum(['surcharge', 'cost'])),
})

/**
 * Cửa danh sách LUÔN lọc theo MỘT đơn. `jobId` bắt buộc, không có dạng "liệt kê cả bảng" — bảng
 * này toàn giá vốn, một cửa quét cả bảng là thứ không nên tồn tại.
 */
export const ListCostQuery = z.object({ jobId: Uuid })

export const CostIdParams = z.object({ id: Uuid })

/** Thân RỖNG nhưng vẫn khai lược đồ (`route-phai-khai-luoc-do`); `version` là toàn bộ nội dung. */
export const CostStepBody = z.object({ version: Version })

/** 📁 Đổi trạng thái hồ sơ của một khoản (bản cũ `setDoc`, `Az.tsx:10096`). */
export const CostDocBody = z.object({ version: Version, docStatus: DocStatus })
export type CostDocBodyT = z.infer<typeof CostDocBody>

export type ListCostQueryT = z.infer<typeof ListCostQuery>
export type CreateCostBodyT = z.infer<typeof CreateCostBody>
export type UpdateCostBodyT = z.infer<typeof UpdateCostBody>

/* ══ GIÁ ĐƯỜNG SẮT · ĐỐI SOÁT NHÀ VẬN CHUYỂN (F25) ════════════════════════════════════════════
 *
 * ⚠ CŨNG LÀ GIÁ VỐN. Bảng giá là giá MUA, bảng kê đối soát là số nhà vận chuyển đòi — cả hai đều
 *   thuộc phần chỉ người có quyền xem giá vốn được nhìn.
 */

/** Nhập giá theo DÒNG VĂN BẢN dán từ Excel, không phải mảng ô — người nhập dán cả cột một lần. */
export const ImportRateBody = z.object({
  vendorId: Uuid,
  effectiveFrom: ISODate,
  lines: z.array(z.string()).min(1, 'Chưa dán dòng giá nào.').max(2000, 'Tối đa 2000 dòng mỗi lần.'),
  note: blankAsAbsent(z.string()),
  /** Nhóm của cả phiên (`0088`) — Ga–Ga · RF lạnh · xe đường ngắn · phụ phí. Bỏ trống = để màn tự đoán. */
  group: blankAsAbsent(z.enum(['gaga', 'rf', 'short', 'sur'])),
})

export const RateBatchQuery = z.object({ limit: PageQuery.shape.limit })

const RateSheetCells = {
  fromPlace: z.string().trim().min(1, 'Thiếu ga đi / điểm đi.'),
  toPlace: z.string().trim().min(1, 'Thiếu ga đến / điểm đến.'),
  ward: z.string().nullable(),
  ward2: z.string().nullable(),
  contType: z.string().nullable(),
  priceFull: moneyField('Giá nguyên cont'),
  priceHalf: moneyField('Giá nửa cont').nullable(),
  transitHours: z.number().min(0).max(1000).nullable(),
  effectiveFrom: ISODate,
  note: z.string().nullable(),
}

/** ⬆ Nạp lại bảng giá đã sửa trong Excel (`azRailPriceXlsx.ts`) — dòng có MÃ DÒNG thì sửa, không có thì thêm. */
export const RateSheetBody = z.object({
  mode: z.enum(['overwrite', 'newPeriod']),
  effectiveFrom: blankAsAbsent(ISODate),
  updates: z.array(z.object({ id: Uuid, ...RateSheetCells })).max(5000),
  adds: z.array(z.object({ vendorId: Uuid, group: z.enum(['gaga', 'rf', 'short', 'sur']).nullable(), ...RateSheetCells })).max(5000),
})

/**
 * ＋ Thêm MỘT dòng giá vốn (`RateModal` bản cũ) — kể cả dòng phụ phí mới (nhóm `sur`: nơi đi = loại phụ phí, nơi đến = đơn vị tính).
 * ⚠ Giá 0 được nhận: phụ phí mới thêm trống và dòng "liên hệ báo giá" của bảng RATRACO đều mang giá 0.
 */
export const CreateCostRateBody = z.object({
  /**
   * ⭐ GÓI DỊCH VỤ của dòng giá vốn (`cost_rate.pack_id`, có từ `0024`).
   * ⚠ Cột này TỪNG KHÔNG AI GHI: chỉ có một `LEFT JOIN` đọc ra `packCode`, nên bộ lọc "Gói" ở bảng
   *   giá vốn luôn rỗng — một ô lọc không lọc được gì. `null` = không gắn gói, vẫn hợp lệ.
   */
  packId: blankAsAbsent(Uuid.nullable()),
  vendorId: Uuid,
  group: z.enum(['gaga', 'rf', 'short', 'sur']).nullable(),
  fromPlace: z.string().trim().min(1, 'Thiếu ga đi / điểm đi.'),
  toPlace: z.string().trim().min(1, 'Thiếu ga đến / điểm đến (phụ phí: đơn vị tính).'),
  contType: z.string().nullable(),
  priceFull: moneyField('Giá nguyên cont'),
  priceHalf: moneyField('Giá nửa cont').nullable(),
  transitHours: z.number().min(0).max(1000).nullable(),
  effectiveFrom: ISODate,
  fuelPrice: moneyField('Giá dầu').nullable(),
  ward: z.string().nullable(),
  ward2: z.string().nullable(),
  note: z.string().nullable(),
})
export type CreateCostRateBodyT = z.infer<typeof CreateCostRateBody>

/**
 * ✏️ Sửa MỘT dòng giá vốn. ⚠ KHÔNG có giá: giá đã có KHOÁ sửa tại chỗ (user 28/07) — đổi giá phải qua "📈 Tạo phiên giá mới".
 * Chỉ sửa thông tin kèm theo (thầu phụ, tuyến, cont, giờ chạy, ngày hiệu lực, giá dầu, phường/xã, ghi chú).
 */
export const UpdateCostRateBody = z.object({
  /** `null` = GỠ gói khỏi dòng; vắng khoá = không đụng tới. */
  packId: blankAsAbsent(Uuid.nullable()),
  version: Version,
  vendorId: blankAsAbsent(Uuid),
  fromPlace: z.string().trim().min(1, 'Thiếu ga đi / điểm đi.').optional(),
  /** `null` = XOÁ TRẮNG đơn vị tính của PHỤ PHÍ (bản cũ lưu ĐVT rỗng, `Az.tsx:20829`); dòng giá cước thì máy chủ chặn. */
  toPlace: z.string().trim().min(1, 'Thiếu ga đến / điểm đến (phụ phí: đơn vị tính).').nullable().optional(),
  contType: z.string().nullable().optional(),
  transitHours: z.number().min(0).max(1000).nullable().optional(),
  effectiveFrom: blankAsAbsent(ISODate),
  fuelPrice: blankAsAbsent(moneyField('Giá dầu')),
  ward: z.string().nullable().optional(),
  ward2: z.string().nullable().optional(),
  note: z.string().nullable().optional(),
})
export type UpdateCostRateBodyT = z.infer<typeof UpdateCostRateBody>

/** 🗑 Xoá dòng giá vốn. `dry` = chỉ đếm dòng giá công bố trỏ tới nó để hỏi lại, chưa xoá gì. */
export const DeleteCostRateBody = z.object({ version: Version, dry: z.boolean() })
export type DeleteCostRateBodyT = z.infer<typeof DeleteCostRateBody>

/** 📈 Tạo phiên giá vốn mới theo ±% — mỗi dòng mang MÃ dòng gốc + giá kỳ mới màn đã tính (`NewCostPeriodModal`). */
export const NewCostPeriodBody = z.object({
  effectiveFrom: ISODate,
  fuelPrice: moneyField('Giá dầu DO'),
  defaultPct: z.number().min(-100).max(1000),
  rows: z.array(z.object({
    id: Uuid,
    priceFull: moneyField('Giá vốn mới'),
    priceHalf: moneyField('Giá nửa cont').nullable(),
  })).min(1, 'Không có dòng giá nào để tạo phiên mới.').max(5000),
})
export type NewCostPeriodBodyT = z.infer<typeof NewCostPeriodBody>

/** Giá vốn đọc theo TRANG có con trỏ — màn phân tích đọc hết mọi trang rồi mới tính. */
export const CostRateQuery = PageQuery

export const RateLookupQuery = z.object({
  vendorId: Uuid,
  fromPlace: z.string().min(1, 'Chưa chọn nơi đi.'),
  toPlace: z.string().min(1, 'Chưa chọn nơi đến.'),
  onDate: ISODate,
  contType: blankAsAbsent(z.string()),
})

/**
 * Một dòng bảng kê nhà vận chuyển gửi. Số tiền LÀ SỐ CỦA HỌ, chưa qua bất kỳ phép sửa nào.
 * ⚠ SỐ CONT ĐƯỢC TRỐNG KHI CÓ MÃ ĐƠN: dòng "bổ sung cước kỳ trước" chỉ có diễn giải + tiền, người đối soát gõ tay mã đơn
 *   để khớp thẳng (user chốt 23/08, `azRailRecon.ts:167-174,242`). Thiếu CẢ HAI thì không có gì để khớp ⇒ từ chối.
 */
export const RailStmtRowIn = z.object({
  rowNo: z.number().int().min(1),
  jobRef: blankAsAbsent(z.string()),
  txnDate: blankAsAbsent(ISODate),
  contNo: z.string(),
  /** Loại cont trên bảng kê — chỉ để BÁO LỆCH với đơn, không dùng để khớp. */
  contType: blankAsAbsent(z.string()),
  train: blankAsAbsent(z.string()),
  plateLoad: blankAsAbsent(z.string()),
  plateDrop: blankAsAbsent(z.string()),
  feeLoad: moneyField('Cước bốc'),
  feeRail: moneyField('Cước đường sắt'),
  feeDrop: moneyField('Cước hạ'),
  feeExtra: moneyField('Phí khác'),
  feeCrane: moneyField('Phí cẩu'),
  total: moneyField('Tổng cộng'),
}).refine((r) => r.contNo.trim() !== '' || (r.jobRef ?? '').trim() !== '',
  { message: 'Dòng thiếu cả số container lẫn mã đơn.', path: ['contNo'] })

/**
 * ⭐ LẤY SỐ NHÀ VẬN CHUYỂN cho những dòng đã tick.
 * ⚠ CHỈ GỬI SỐ DÒNG, không gửi số tiền. Máy chủ tự tính lại từ bảng kê đã lưu — nhận tiền từ màn
 *   là mở cửa cho người gọi đặt giá vốn bất kỳ, đi vòng qua cả bảng kê lẫn luật khoá khoản.
 */
export const ApplyRailFiguresBody = z.object({
  rowNos: z.array(z.number().int().min(1)).min(1, 'Chưa chọn dòng nào để lấy số.')
    .max(2000, 'Tối đa 2000 dòng mỗi lượt.'),
})

export type ApplyRailFiguresBodyT = z.infer<typeof ApplyRailFiguresBody>

/** 👁 Đối soát "Mọi thầu phụ" CHỈ XEM (V02139) — gửi các dòng của tệp, máy chủ dò + so tiền, KHÔNG lưu đợt. */
export const RailPreviewBody = z.object({
  rows: z.array(RailStmtRowIn).min(1, 'Bảng kê không có dòng nào.').max(2000, 'Tối đa 2000 dòng mỗi đợt.'),
})
export type RailPreviewBodyT = z.infer<typeof RailPreviewBody>

export const CreateRailStmtBody = z.object({
  vendorId: Uuid,
  periodFrom: ISODate,
  periodTo: ISODate,
  rows: z.array(RailStmtRowIn).min(1, 'Bảng kê không có dòng nào.').max(2000, 'Tối đa 2000 dòng mỗi đợt.'),
  note: blankAsAbsent(z.string()),
})

/**
 * 🔍 ĐỐI SOÁT GIÁ THẦU PHỤ với bảng giá — lọc theo NGÀY CHẠY của chặng.
 *
 * ⚠ HAI NGÀY TUỲ CHỌN, và đó KHÔNG phải sự lỏng lẻo — hai lý do cùng chỉ về một chỗ:
 *   ① Bản cũ ghi thẳng trong chú thích ô lọc: *"Mặc định THÁNG NÀY"*
 *     (`azPurchase.vendorPriceAudit.dateFilterInfo`), nên gọi trần PHẢI ra tháng này.
 *   ② `tools/rt-stubs` gọi MỌI `rtPath` bằng URL trần rồi đòi 200/403; bắt buộc tham số là màn
 *     này trả 400 và cổng đỏ vì một lý do không liên quan. Tiền lệ `costs.ts`, `stats.ts`.
 * ⚠ Trần một năm vẫn còn, nhưng ở MIỀN — nơi biết cả hai mốc sau khi đã điền mặc định.
 */
export const PriceAuditQuery = z.object({
  from: blankAsAbsent(ISODate),
  to: blankAsAbsent(ISODate),
})

export const RailStmtIdParams = z.object({ id: Uuid })
export const RailStmtQuery = PageQuery
/** ⬇ Mẫu bảng kê MH gửi nhà thầu (in-excel-13) — danh sách đơn của MỘT nhà thầu, không chặn kỳ (bản cũ `Az.tsx:25620-25640`). */
/** `vendorId` vắng = chưa chọn nhà thầu ⇒ mẫu gồm MỌI đơn có chặng (bản cũ `Az.tsx:25622-25627`, V02169). */
export const MhTemplateQuery = z.object({ vendorId: blankAsAbsent(Uuid) })
export type MhTemplateQueryT = z.infer<typeof MhTemplateQuery>
/** Người soát CHỌN TAY đơn cho một dòng "khớp không chắc"; `jobRef` rỗng = gỡ lựa chọn. */
export const RailPickJobBody = z.object({
  rowNo: z.coerce.number().int().min(1),
  jobRef: z.string().max(60),
})

export type RailPickJobBodyT = z.infer<typeof RailPickJobBody>

export const RailReviewBody = z.object({ version: Version })

/**
 * Thân RỖNG nhưng VẪN khai, theo quy ước `route-phai-khai-luoc-do`: hai cửa `soát` và `gỡ phiên`
 * không cần dữ liệu gì, nhưng khai lược đồ vẫn có tác dụng — khoá lạ gửi kèm bị gạt đi thay vì đi
 * tiếp vào tay người viết cửa sau này.
 */
export const RailNoBody = z.object({})

export type ImportRateBodyT = z.infer<typeof ImportRateBody>
export type RateBatchQueryT = z.infer<typeof RateBatchQuery>
export type CostRateQueryT = z.infer<typeof CostRateQuery>
export type RateLookupQueryT = z.infer<typeof RateLookupQuery>
export type CreateRailStmtBodyT = z.infer<typeof CreateRailStmtBody>
export type PriceAuditQueryT = z.infer<typeof PriceAuditQuery>
export type RailStmtQueryT = z.infer<typeof RailStmtQuery>

/* ══ NGOẠI LỆ GIÁ VỐN ĐƯỜNG SẮT ═══════════════════════════════════════════════════════════════ */

/**
 * ⚠ KHÔNG có `stationKey`/`wardKey`/`addrKey` ở thân yêu cầu: khoá dò do MÁY CHỦ sinh. Cho lời gọi
 *   tự tính khoá là mở đường cho hai phép chuẩn hoá khác nhau, và khi đó ghim xong không dò lại
 *   được — hỏng im lặng.
 */
export const PinRailPriceExBody = z.object({
  vendorId: blankAsAbsent(Uuid),
  station: z.string().min(1, 'Thiếu tên ga.'),
  province: blankAsAbsent(z.string()),
  ward: blankAsAbsent(z.string()),
  addr: z.string().min(1, 'Thiếu địa chỉ điểm lấy/giao.'),
  amount: moneyField('Giá vốn ngoại lệ'),
  effectiveFrom: ISODate,
  jobRef: blankAsAbsent(z.string()),
  note: blankAsAbsent(z.string()),
  basedOnId: blankAsAbsent(Uuid),
})

export const PinLegCustomerExBody = z.object({
  vendorId: blankAsAbsent(Uuid),
  customerId: Uuid,
  fromStation: z.string().min(1, 'Thiếu ga đi.'),
  toStation: z.string().min(1, 'Thiếu ga đến.'),
  /**
   * Loại cont của đơn (`contType || contSize || ''`, `Az.tsx:5221`) — ĐƯỢC TRỐNG như bản cũ: khoá dò chỉ có hai lớp 40 / 40RF
   * (`contKey`), cont trống thuộc lớp 40. Bắt buộc ở đây là đơn Ga↔Ga chưa có loại cont không ghim được ("Thiếu loại cont.").
   */
  cont: z.string().max(50),
  amount: moneyField('Giá vốn ngoại lệ'),
  effectiveFrom: ISODate,
  jobRef: blankAsAbsent(z.string()),
  note: blankAsAbsent(z.string()),
})

export const SetExActiveBody = z.object({ version: Version, active: z.boolean() })
export const ExIdParams = z.object({ id: Uuid })

export type PinRailPriceExBodyT = z.infer<typeof PinRailPriceExBody>
export type PinLegCustomerExBodyT = z.infer<typeof PinLegCustomerExBody>
export type SetExActiveBodyT = z.infer<typeof SetExActiveBody>

export type RateSheetBodyT = z.infer<typeof RateSheetBody>

/* ══ 🚚 BẮN CHẶNG HÀNG LOẠT (`Az.tsx` `BulkAssignLegModal` · `applyDispatch`) ═══════════════════════════════════════ */

export const DispatchPlanQuery = z.object({
  /**
   * KHÔNG BẮT BUỘC (vá 18/09/2026) — bản cũ `BulkAssignLegModal` dựng LƯỚI ĐƠN ngay khi mở hộp,
   * chọn thầu phụ chỉ BƠM GIÁ vào các ô đã có. Bắt buộc `vendorId` ở đây là hộp mở ra trống trơn
   * "Chọn thầu phụ trước", điều hành không soát được mình đang bắn đơn nào. Thiếu nó thì kế hoạch
   * vẫn trả nhãn chặng · tuyến · số chặng đã có, giá để 0 cho người dùng tự nhập.
   */
  vendorId: blankAsAbsent(Uuid),
  /** Mã đơn cách nhau dấu phẩy — tối đa `ORDER_BULK_MAX` đơn một lần bắn (bản cũ `BulkAssignLegModal` nhận mọi đơn đã chọn). */
  jobIds: z.string().min(1, 'Chưa chọn đơn nào.'),
  nLeg: blankAsAbsent(z.coerce.number().int().min(1).max(5)),
  /**
   * ✏️ SỬA CHUYẾN của thầu phụ này — chặng ĐẦU của lần bắn lại = vị trí gốc của chặng NCC đó (min seq − 1), không nối tiếp sau
   * chính chặng sắp bị thay (bản cũ `baseSeqOf` + `baseOverride`, `Az.tsx:5089-5096 · 8100`; rà 16/10 T37: nhãn "Chặng 2" ghi
   * vào CSDL dù seq = 1).
   */
  editVendorId: blankAsAbsent(Uuid),
  /**
   * NGÀY LẤY HÀNG điều hành sửa trong hộp — `jobId:YYYY-MM-DD` cách nhau dấu phẩy. Phí cẩu hạ tra theo ngày này (bản cũ
   * `railCraneSurcharges(…, puDateOf(j))`, `Az.tsx:5054,6201`); đơn không có trong chuỗi thì lấy giờ lấy của điểm lấy 1, rồi
   * ngày kế hoạch.
   */
  puDates: blankAsAbsent(z.string().max(20_000)),
})
export type DispatchPlanQueryT = z.infer<typeof DispatchPlanQuery>

const DispatchMoney = moneyField('Số tiền')

export const DispatchBody = z.object({
  vendorId: Uuid,
  settleMode: z.enum(['monthly', 'immediate']),
  /** Công ty tự xác nhận thay thầu phụ ngay lúc bắn (user 20/08). */
  autoConfirm: z.boolean(),
  /**
   * ✏️ SỬA CHUYẾN — GỠ chặng cũ của ĐÚNG thầu phụ này trên các đơn trong lô rồi bắn lại
   * (bản cũ `editTrip`, `Az.tsx:5298`). Mặc định `false` = bắn THÊM chuyến mới như cũ.
   *
   * ⚠ Gỡ đi là MẤT lịch sử chặng cũ — bản cũ cũng cảnh báo đúng vậy (`editTripNoHistoryTip`).
   *   Đơn nào có chặng đang bị KHOÁ (đã giao · kế toán đã xác nhận chi phí · thầu phụ đã xác nhận)
   *   thì BỎ QUA cả đơn kèm lý do, không gỡ nửa chừng.
   */
  replace: z.boolean().optional(),
  /**
   * ✏️ THẦU PHỤ CỦA CHUYẾN ĐANG SỬA — chặng/chi phí bị gỡ là của NCC NÀY, kể cả khi trong hộp người dùng đổi sang NCC khác
   * (bản cũ gỡ theo `editTrip.vendorId` rồi bắn lại cho NCC mới ở đúng vị trí cũ, `Az.tsx:8071-8104`). Thiếu thì lấy `vendorId`.
   */
  editVendorId: blankAsAbsent(Uuid),
  jobs: z.array(z.object({
    jobId: Uuid,
    pickupDate: blankAsAbsent(ISODate),
    contNo: blankAsAbsent(z.string().max(50)),
    sealNo: blankAsAbsent(z.string().max(50)),
    /** Ghi chú giải thích phụ phí — 1 ô/đơn, gắn vào khoản cước chặng đầu (user 23/08). */
    note: blankAsAbsent(z.string().max(500)),
    legs: z.array(z.object({
      label: z.string().min(1).max(200),
      fromAddr: z.string().max(500),
      toAddr: z.string().max(500),
      amount: DispatchMoney,
      plate: blankAsAbsent(z.string().max(100)),
      rateId: blankAsAbsent(Uuid),
      priceMode: z.enum(['bid', 'nego']),
      surcharges: z.array(z.object({ label: z.string().min(1).max(200), amount: DispatchMoney })).max(5),
    })).min(1).max(5),
    crane: z.array(z.object({ label: z.string().min(1).max(200), amount: DispatchMoney })).max(2),
  })).min(1).max(ORDER_BULK_MAX, BULK_TOO_MANY),
})
export type DispatchBodyT = z.infer<typeof DispatchBody>

/**
 * ⬆️ NHẬP GIÁ NCC HÀNG LOẠT theo MÃ ĐƠN (`BulkCostImport`, user 15/07 · template 19/07): mỗi dòng ≤ 5 chặng (NCC + giá) + ≤ 5 phụ phí
 * (phụ phí i gắn chặng i) + ghi chú. Màn dò NCC theo mã / tên; `vendorId` rỗng ⇒ máy chủ báo "Không tìm thấy NCC".
 */
export const BulkCostImportBody = z.object({
  rows: z.array(z.object({
    ref: z.string().min(1).max(50),
    legs: z.array(z.object({ vendorId: Uuid.nullable(), vendorLabel: z.string().max(200), amount: z.string().max(30) })).max(5),
    surcharges: z.array(z.string().max(30)).max(5),
    note: blankAsAbsent(z.string().max(500)),
  })).min(1, 'Chưa có dòng nào.').max(BULK_COST_IMPORT_MAX),
  dryRun: z.boolean(),
})
export type BulkCostImportBodyT = z.infer<typeof BulkCostImportBody>
